import { useState } from 'react';
import { cn } from '@localagent/ui/lib/utils';
import { Button } from '@localagent/ui/components/button';
import { getSDK } from '../../services/sdk-instance';
import type { ErrorEvent } from '@localagent/sdk';
import { resolveServiceName } from './error-messages';

const SEVERITY_DOT: Record<string, string> = {
  warning: 'bg-yellow-500',
  error: 'bg-red-500',
  critical: 'bg-red-500',
};

export function ErrorHistoryTab() {
  const [errors, setErrors] = useState<ErrorEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchErrors = async () => {
    setLoading(true);
    try {
      const start_time = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const result = await getSDK().errors.listErrors({ start_time, limit: 50 });
      setErrors(result.errors);
      setTotal(result.total);
      setLoaded(true);
    } catch (error) {
      console.error('Failed to fetch error history:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="error-history-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Error History</h2>
          <p className="text-sm text-muted-foreground">
            Error events from the last 24 hours
          </p>
        </div>
        <Button onClick={() => { void fetchErrors(); }} disabled={loading} size="sm">
          {loading ? 'Loading...' : loaded ? 'Refresh' : 'Load Errors'}
        </Button>
      </div>

      {loaded && (
        <div className="rounded-lg">
          {errors.length === 0 ? (
            <div className="flex h-20 items-center justify-center text-sm text-muted-foreground">
              No errors in the last 24 hours
            </div>
          ) : (
            <>
              <div>
                {errors.map(err => {
                  const severity = err.severity ?? 'error';
                  return (
                    <div key={err.event_id} className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className={cn('inline-block h-2 w-2 flex-shrink-0 rounded-full', SEVERITY_DOT[severity] ?? 'bg-red-500')} />
                        <span className="text-xs font-medium text-muted-foreground">
                          {resolveServiceName(err.service_origin)}
                        </span>
                        <code className="ml-1 text-xs text-muted-foreground">{err.error_code}</code>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {new Date(err.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="mt-1 text-sm">{err.message}</p>
                    </div>
                  );
                })}
              </div>
              {total > errors.length && (
                <div className="px-4 py-2 text-center text-xs text-muted-foreground">
                  Showing {errors.length} of {total} errors
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
